import { get, patch, post } from 'utils/functions/request';
import urls from 'utils/urls';
import type {
  DiscountActionType,
  DiscountAllowedAction,
  DiscountItem,
  DiscountProduct,
  DiscountStatus,
  DiscountValueType,
  RawDiscountItem,
  RawDiscountListResponse,
} from './types/discount.types';
import { DEFAULT_ACTION_LABELS, PRODUCT_TYPE, STATUS_TYPE_TO_STRING_MAP } from './constants';

const discountsBase = urls.pharmacyModuleUrls.discountsUrls.base;

function normalizeAllowedAction(action: DiscountActionType): DiscountAllowedAction {
  return {
    type: action,
    label: DEFAULT_ACTION_LABELS[action],
  };
}

const OFFER_STATUS_MAP: Record<number, DiscountStatus> = {
  1: 'Scheduled',
  2: 'Active',
  3: 'Stopped',
  4: 'Completed',
};

const OFFER_DISCOUNT_TYPE_MAP: Record<number, DiscountValueType> = {
  1: 'percent',
  2: 'amount',
};

function normalizeDiscountItem(raw: RawDiscountItem): DiscountItem {
  const status = OFFER_STATUS_MAP[raw.status] ?? 'Scheduled';
  const product = raw.product;
  const productType = raw.productType ?? product?.productType ?? null;

  return {
    id: raw.offerKey,
    status,
    allowedActions: raw.allowedActions.map(normalizeAllowedAction),
    discountPercent: raw.discountPercentage,
    discountValue: raw.discountValue ,
    discountType: OFFER_DISCOUNT_TYPE_MAP[raw.discountType] ,
    productKey: raw.productKey ,
    productName: product.nameEn,
    productImageUrl: product.mainImageUrl,
    originalPrice: raw.price,
    discountedPrice: raw.priceAfterDiscount,
    startDate: raw.startDate,
    endDate: raw.endDate,
    inStock: raw.stockQuantity > 0,
    productType,
    isBundle: productType === PRODUCT_TYPE.Bundle,
  };
}

function extractDescription(raw: Record<string, unknown>): string | null {
  if (!Array.isArray(raw.productTranslators)) return null;
  const translators = raw.productTranslators as { lang?: string; description?: string | null }[];
  const en = translators.find(t => t.lang === 'en' && t.description);
  return en?.description ?? translators.find(t => t.description)?.description ?? null;
}

function normalizeDiscountListResponse(response: unknown) {
  const payload = (response as { data?: RawDiscountListResponse })?.data ?? response ?? {};
  const record = payload as Partial<RawDiscountListResponse>;
  const items = record.items;

  const list = Array.isArray(items) ? items : [];
  return {
    items: list.map(item => normalizeDiscountItem(item as RawDiscountItem)),
    totalCount: list.length,
    pageSize: record.pageSize,
  };
}

export const getDiscountsList = (params: Record<string, unknown>) => {
  return get(null, false, discountsBase, params).then(
    normalizeDiscountListResponse,
  );
};

export const getOffersProducts = (
  params: Record<string, unknown>,
) => {
  return get(null, false, urls.pharmacyModuleUrls.discountsUrls.offersProducts, params).then(
    normalizeDiscountListResponse,
  );
};


export const createDiscount = (data: Record<string, unknown>): Promise<unknown> => {
  return post(null, false, urls.pharmacyModuleUrls.discountsUrls.createOffer, data);
};

export const updateDiscount = ({
  discountId,
  data,
}: {
  discountId: string | number;
  data: Record<string, unknown>;
}): Promise<unknown> => {
  return patch(null, false, `${discountsBase}/${discountId}`, data);
};


export const performOfferAction = ({
  offerKey,
  actionType,
  discountType,
  discountValue,
  startDate,
  endDate,
}: {
  offerKey: string;
  actionType: string;
  discountType?: string;
  discountValue?: number;
  startDate?: string;
  endDate?: string;
}): Promise<unknown> => {
  return post(
    null,
    false,
    `${urls.pharmacyModuleUrls.discountsUrls.offerActionsBase}/${offerKey}/actions`,
    { actionType, discountType, discountValue, startDate, endDate },
  );
};

export const searchEligibleProducts = ({
  searchTerm,
  pageNumber = 1,
  pageSize = 20,
  productType,
  offerEligibleOnly,
}: {
  searchTerm: string;
  pageNumber?: number;
  pageSize?: number;
  productType?: number;
  offerEligibleOnly?: boolean;
}) => {
  const params: Record<string, unknown> = {
    SearchTerm: searchTerm,
    PageNumber: pageNumber,
    PageSize: pageSize,
  };
  if (productType != null) {
    params.productType = productType;
  }
  if (offerEligibleOnly) {
    params.OfferEligibleOnly = true;
  }

  return get(null, false, urls.pharmacyModuleUrls.discountsUrls.eligibleProducts, params).then(response => {
    const payload = (response as { data?: unknown })?.data ?? response ?? {};
    const record = payload as Record<string, unknown>;
    const items = Array.isArray(payload) ? payload : (record.items ?? []);
    const list = Array.isArray(items) ? items : [];
    return {
      items: list.map(item => {
        const raw = item as Record<string, unknown>;
        const offer = raw.offer as Record<string, unknown> | undefined | null;
        const offerStatus = offer?.status as number | undefined;
        const mappedStatus = offerStatus ? OFFER_STATUS_MAP[offerStatus] : null;
        const mappedProductType = raw.productType != null ? Number(raw.productType) : null;
        return {
          productKey: String(raw.key ?? ''),
          productShapeId: 0,
          productName: String(raw.nameEn ?? raw.name ?? 'Unknown product'),
          productShapeName: '',
          productImageUrl: raw.mainImageUrl ? String(raw.mainImageUrl) : '',
          originalPrice: Number(raw.price ?? raw.newPrice ?? 0),
          productType: mappedProductType,
          description: extractDescription(raw),
          existingOfferStatus: mappedStatus,
          existingOfferStatusLabel: mappedStatus ? (STATUS_TYPE_TO_STRING_MAP[mappedStatus] ?? null) : null,
        } as DiscountProduct;
      }),
      totalCount: Number(record.totalCount ?? record.total ?? list.length),
      pageNumber: Number(record.pageNumber ?? record.page ?? pageNumber),
      pageSize: Number(record.pageSize ?? pageSize),
    };
  });
};

export const searchDiscountProducts = (query: string): Promise<DiscountProduct[]> => {
  return get(null, false, urls.pharmacyModuleUrls.commonUrls.getProducts, {
    searchTerm: query,
    pageNumber: 1,
    pageSize: 20,
  }).then(response => {
    const payload = (response as { data?: unknown })?.data ?? response ?? {};
    const record = payload as Record<string, unknown>;
    const items = Array.isArray(payload) ? payload : (record.items ?? record.data ?? []);
    const list = Array.isArray(items) ? items : [];
    return list.map(item => {
      const raw = item as Record<string, unknown>;
      return {
        productKey: String(raw.productKey ?? raw.key ?? ''),
        productShapeId: Number(raw.id ?? raw.productShapeId ?? 0),
        productName: String(raw.productNameEn ?? raw.productName ?? raw.name ?? ''),
        productShapeName: String(raw.productShapeTypeName ?? raw.shapeName ?? ''),
        productImageUrl: String(raw.mainImageUrl ?? raw.imageUrl ?? ''),
        originalPrice: Number(raw.newPrice ?? raw.price ?? 0),
      };
    });
  });
};
